import { FunctionComponent, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isDOMAvailable } from '../../helpers';
import {
    isMessengerIntegration,
    selectIsDebugToolsEnabled,
    selectIsMeetingRoomIntegration,
    selectIsYandexNet,
} from '../config/selectors';
import { getPromozavrRendererSource } from './renderer-source';
import {
    buildPromozavrDebugSnapshot,
    computePromozavrRuntimeDecision,
    shouldDispatchPromozavrInit,
    shouldResetPromozavrState,
    syncPromozavrDebugSnapshot,
} from './runtime-helpers';
import {
    selectPromozavrImages,
    selectPromozavrIsSelected,
    selectPromozavrLastError,
    selectPromozavrRendererSource,
    selectPromozavrRequest,
    selectPromozavrRequestKey,
    selectPromozavrSelectedPromo,
    selectPromozavrStatus,
    selectPromozavrTranslations,
    selectShouldUsePaas,
    selectShouldUsePdokPromoRenderer,
} from './selectors';
import { initPromozavr, resetPromozavrState, setPromozavrRendererSource } from './slice';

declare global {
    interface Window {
        __telemostPromozavr?: Record<string, unknown>;
    }
}

export const PromozavrRuntime: FunctionComponent = () => {
    const dispatch = useDispatch();
    const { pathname, search } = useLocation();
    const shouldUsePaas = useSelector(selectShouldUsePaas);
    const shouldUsePdokPromoRenderer = useSelector(selectShouldUsePdokPromoRenderer);
    const isDebugToolsEnabled = useSelector(selectIsDebugToolsEnabled);
    const isYandexNet = useSelector(selectIsYandexNet);
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);
    const isMessenger = useSelector(isMessengerIntegration);
    const request = useSelector(selectPromozavrRequest);
    const requestKey = useSelector(selectPromozavrRequestKey);
    const selectedPromo = useSelector(selectPromozavrSelectedPromo);
    const translations = useSelector(selectPromozavrTranslations);
    const images = useSelector(selectPromozavrImages);
    const isSelected = useSelector(selectPromozavrIsSelected);
    const rendererSource = useSelector(selectPromozavrRendererSource);
    const status = useSelector(selectPromozavrStatus);
    const lastError = useSelector(selectPromozavrLastError);

    const { hasDebugQuery, shouldInit, nextRequestKey, nextRendererSource } = computePromozavrRuntimeDecision(
        {
            pathname,
            search,
            isYandexNet,
            shouldUsePaas,
            shouldUsePdokPromoRenderer,
            isMessenger,
            isMeetingRoom: isMeetingRoomIntegration,
        },
        getPromozavrRendererSource
    );

    useEffect(() => {
        if (
            shouldResetPromozavrState({
                shouldInit,
                status,
                request,
                requestKey,
                selectedPromo,
                translations,
                images,
                isSelected,
                rendererSource,
                lastError,
                nextRendererSource,
            })
        ) {
            dispatch(resetPromozavrState());
            return;
        }

        if (rendererSource !== nextRendererSource) {
            dispatch(setPromozavrRendererSource(nextRendererSource));
        }
    }, [
        dispatch,
        images,
        isSelected,
        lastError,
        nextRendererSource,
        rendererSource,
        request,
        requestKey,
        selectedPromo,
        shouldInit,
        status,
        translations,
    ]);

    useEffect(() => {
        if (!shouldDispatchPromozavrInit({ shouldInit, requestKey, nextRequestKey })) {
            return;
        }

        dispatch(
            initPromozavr({
                pathname,
                search,
                requestKey: nextRequestKey,
            })
        );
    }, [dispatch, nextRequestKey, pathname, requestKey, search, shouldInit]);

    useEffect(() => {
        if (!isDOMAvailable()) {
            return;
        }

        syncPromozavrDebugSnapshot(
            window,
            buildPromozavrDebugSnapshot({
                pathname,
                shouldInit,
                status,
                requestKey,
                request,
                selectedPromo,
                translations,
                images,
                isSelected,
                rendererSource,
                lastError,
            }),
            hasDebugQuery || isDebugToolsEnabled
        );
    }, [
        hasDebugQuery,
        images,
        isDebugToolsEnabled,
        isSelected,
        lastError,
        pathname,
        rendererSource,
        request,
        requestKey,
        selectedPromo,
        shouldInit,
        status,
        translations,
    ]);

    return null;
};
